import { useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { getUserOptions } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus, Settings, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Access } from '@/config/access.config';
import { Button } from '@/components/ui/button';
import StudentTable from '@/components/custom-tables/student.table';
import { useAccess } from '@/components/guards/use-access';
import CreateUpdateStudentModal from '@/components/modals/create-update-student-modal';
import StudentSheet from '@/components/sheets/student-sheet/student-sheet';
import StudentImportModal from '@/components/student-import/student-import-modal';

const studentRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  student: z.number().optional(),
});

const VISIBLE_TO_ROLES = [UserRoles.Root, UserRoles.Admin, UserRoles.Secretary, UserRoles.Parent];

export const Route = createFileRoute('/_app/(students)/students/')({
  validateSearch: zodSearchValidator(studentRouteSchema),
  component: Students,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (!VISIBLE_TO_ROLES.includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function Students() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <StudentsTabHeader />
      <StudentsTable />
    </div>
  );
}

function StudentsTabHeader() {
  const navigate = useNavigate();
  const canUseStudentsExcelImport = useAccess(Access.StudentsExcelImport);

  const [importOpen, setImportOpen] = useState(false);

  const { t } = useTranslation(['student', 'common']);

  const createSubject = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  const navigateToSettings = () => {
    navigate({ to: '/students/settings' });
  };

  return (
    <div className='flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-wrap items-center gap-2 sm:gap-3'>
        {canUseStudentsExcelImport && (
          <Button
            type='button'
            variant='outline'
            size='default'
            className='flex items-center gap-2 whitespace-nowrap'
            onClick={() => setImportOpen(true)}
          >
            <Upload className='h-4 w-4' />
            <span>Import Excel</span>
          </Button>
        )}
        <Button
          className='flex items-center gap-2 whitespace-nowrap'
          onClick={createSubject}
          size='default'
        >
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('student', { ns: 'common' }))}
        </Button>
        <Button
          className='flex items-center gap-2 whitespace-nowrap'
          onClick={navigateToSettings}
          size='default'
        >
          <Settings className='h-6 w-6' />
          {toCapitalize(t('settings', { ns: 'common' }))}
        </Button>
      </div>
      <CreateUpdateStudentModal />
      <StudentImportModal open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}

function StudentsTable() {
  return (
    <>
      <StudentTable />
      <StudentSheet />
    </>
  );
}
